import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';

/**
 * FloatingWidgetDock
 * Mar 2, 2026 — Multiple floating widgets were stacking in the bottom-right
 * corner and competing for z-index (KeyboardCheatsheet, FloatingBugReportButton,
 * OfflineQueue trigger, etc.). This dock collects them into a single collapsible
 * column so:
 *   • The corner stays visually clean
 *   • Provider can hide the whole dock in one click
 *   • No more z-index whack-a-mole with the Resources/References footer
 *
 * Children stack vertically (top-to-bottom in the DOM = bottom-to-top on screen
 * so the primary CTA is closest to the thumb). Dock remembers its collapsed
 * state per-user via localStorage.
 */
export const FloatingWidgetDock = ({ children }) => {
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('writeright_dock_collapsed') === 'true'; } catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('writeright_dock_collapsed', collapsed ? 'true' : 'false'); } catch { /* ignore */ }
  }, [collapsed]);

  // Filter out null / false / undefined children so callers can conditionally
  // mount widgets without adding empty rows.
  const items = React.Children.toArray(children).filter(Boolean);
  if (items.length === 0) return null;

  return (
    <div
      className="fixed bottom-4 right-4 z-[9500] flex flex-col-reverse items-end gap-2 pointer-events-none"
      data-testid="floating-widget-dock"
    >
      {/* Collapse handle sits at the BOTTOM (thumb-nearest) */}
      <button
        type="button"
        onClick={() => setCollapsed((v) => !v)}
        className="pointer-events-auto w-9 h-9 rounded-full bg-secondary/90 hover:bg-secondary border border-border shadow-lg backdrop-blur flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        data-testid="floating-widget-dock-toggle"
        title={collapsed ? 'Show widgets' : 'Hide widgets'}
      >
        {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {/* Stacked widgets — hidden entirely when collapsed. Each child is
          wrapped so it receives pointer-events-auto (dock container disables
          them by default so gaps don't block clicks below). */}
      {!collapsed && items.map((child, i) => (
        <div key={i} className="pointer-events-auto">
          {child}
        </div>
      ))}
    </div>
  );
};

export default FloatingWidgetDock;
